import Decimal from "decimal.js";
import FormField from "components/FormField/FormField";
import InputAdornment from "@mui/material/InputAdornment";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { useCallback, useMemo, useState } from "react";
import MDButton from "components/MDButton";
import { ProposalDefault, ProposalPlantProduct } from "generated/graphql";
import toFiniteNumber from "utils/number/toFiniteNumber";

interface PlantProductTransferTruckRowProps {
  row: any;
  idx: number;
  productTransferTrucks: any[];
  product: any;
  proposalPlantProduct: ProposalPlantProduct;
  inViewMode: boolean;
  handleSubmit: (input: any) => void;
  handleRemoveTruck: (id: string) => void;
  averageWorkingHoursPerDay: number;
  proposalDefaults: ProposalDefault;
  jobDurationInDays: number;
  compactMode?: boolean;
  showAdvancedColumns?: boolean;
}

const PlantProductTransferTruckRow = ({
  row,
  idx,
  productTransferTrucks,
  product,
  proposalPlantProduct,
  inViewMode,
  handleSubmit,
  handleRemoveTruck,
  averageWorkingHoursPerDay,
  proposalDefaults,
  jobDurationInDays,
  compactMode = false,
  showAdvancedColumns = false,
}: PlantProductTransferTruckRowProps) => {
  // Use round trip time from the proposal plant product (new field)
  const roundTripTime =
    proposalPlantProduct.transferTruckRoundTripTimeHrsOverride ||
    proposalPlantProduct.transferTruckRoundTripTimeHrs ||
    product.transferTruckRoundTripTime;

  const totalTime =
    roundTripTime +
    proposalPlantProduct.transferTruckLoadTimeBuffer +
    proposalPlantProduct.transferTruckUnloadTimeBuffer;

  const roundUpTo1 = (value) => (value < 1 ? 1 : value);
  const roundToTenth = (value) => Math.round(value * 10) / 10;

  // Use material needed from this specific plant product
  const materialNeeded =
    proposalPlantProduct.materialNeededOverride || proposalPlantProduct.materialNeeded;
  const totalTripsNeeded = Math.ceil(roundUpTo1(materialNeeded / row.tonnage));

  const [trucksNeededOverride, setTrucksNeededOverride] = useState(row.quantityOverride || null);
  const [totalHaulingHoursOverride, setTotalHaulingHoursOverride] = useState(
    row.totalHaulingHoursOverride
  );
  const [totalCost, setTotalCost] = useState<number | string | null>(
    row.costOverride ? row.costOverride / 100 : null
  );

  const [rateOverride, setRateOverride] = useState<number | string | null>(
    row.rateOverride ? row.rateOverride / 100 : null
  );
  const hasRateOverride = rateOverride !== null && rateOverride !== "";
  const parsedRateOverride = hasRateOverride ? +rateOverride : null;

  const [truckingProductionRate, setTruckingProductionRate] = useState(row.truckingProductionRate);
  const truckingProductionRateValue = useMemo(() => {
    return toFiniteNumber(truckingProductionRate);
  }, [truckingProductionRate]);

  const calculateMaxTripQuantity = useCallback(() => {
    if (!proposalDefaults.maxTripNumberPerTruck) {
      return null;
    }

    let trucksNeeded = totalTripsNeeded / proposalDefaults.maxTripNumberPerTruck;
    if (jobDurationInDays > 1) {
      trucksNeeded = trucksNeeded / jobDurationInDays;
    }

    return roundToTenth(trucksNeeded);
  }, [proposalDefaults.maxTripNumberPerTruck, totalTripsNeeded, jobDurationInDays]);

  const calculateProductionRateQuantity = useCallback(
    (productionRateValue) => {
      if (productionRateValue <= 0) {
        return null;
      }

      const number = (productionRateValue / row.tonnage) * totalTime;
      return roundToTenth(number > totalTripsNeeded ? totalTripsNeeded : number);
    },
    [row.tonnage, totalTime, totalTripsNeeded]
  );

  const trucksNeededDefault = useMemo(() => {
    if (row.tonnage > materialNeeded) {
      return 1;
    }

    const productionRateQuantity = calculateProductionRateQuantity(row.truckingProductionRate);
    const maxTripQuantity = calculateMaxTripQuantity();

    if (productionRateQuantity !== null && maxTripQuantity !== null) {
      return roundUpTo1(Math.max(productionRateQuantity, maxTripQuantity));
    }

    if (productionRateQuantity !== null) {
      return roundUpTo1(productionRateQuantity);
    }

    if (maxTripQuantity !== null) {
      return roundUpTo1(maxTripQuantity);
    }

    return 1;
  }, [
    materialNeeded,
    row.tonnage,
    row.truckingProductionRate,
    calculateProductionRateQuantity,
    calculateMaxTripQuantity,
  ]);

  const trucksNeededAuto = useMemo(() => {
    if (row.tonnage > materialNeeded) {
      return 1;
    }

    const productionRateQuantity = calculateProductionRateQuantity(truckingProductionRateValue);
    const maxTripQuantity = calculateMaxTripQuantity();

    if (productionRateQuantity !== null && maxTripQuantity !== null) {
      return roundUpTo1(Math.max(productionRateQuantity, maxTripQuantity));
    }

    if (productionRateQuantity !== null) {
      return roundUpTo1(productionRateQuantity);
    }

    if (maxTripQuantity !== null) {
      return roundUpTo1(maxTripQuantity);
    }

    return 1;
  }, [
    materialNeeded,
    row.tonnage,
    truckingProductionRateValue,
    calculateProductionRateQuantity,
    calculateMaxTripQuantity,
  ]);

  const handleProductionRateChange = useCallback(
    (value) => {
      setTruckingProductionRate(value === "" ? row.truckingProductionRate : value);
    },
    [row.truckingProductionRate]
  );

  const handleTrucksNeededChange = useCallback(
    (value) => {
      setTrucksNeededOverride(value);
      const computedValue = value === "" ? +trucksNeededAuto : +value;
      if (Number.isFinite(computedValue) && totalTime > 0) {
        setTruckingProductionRate(Math.round((computedValue * row.tonnage) / totalTime));
      }
    },
    [trucksNeededAuto, row.tonnage, totalTime]
  );

  const hasBeenAdded = !!productTransferTrucks.find(
    (pt) => pt.transferTruckId === row.transferTruckId
  );

  const trucksNeededForCalculations = trucksNeededOverride || trucksNeededAuto;

  const tripsPerTruck = roundUpTo1(+totalTripsNeeded / +trucksNeededForCalculations);
  const [hoursPerTruckOverride, setHoursPerTruckOverride] = useState<number | string | null>(
    row.totalHaulingHoursOverride
      ? (+row.totalHaulingHoursOverride / +(row.quantityOverride || trucksNeededAuto)).toFixed(2)
      : null
  );
  const effectiveTotalHaulingHoursOverride = useMemo(() => {
    if (hoursPerTruckOverride === null || hoursPerTruckOverride === "") {
      return totalHaulingHoursOverride;
    }
    const hoursPerTruck = +hoursPerTruckOverride;
    if (!Number.isFinite(hoursPerTruck)) {
      return totalHaulingHoursOverride;
    }
    return (hoursPerTruck * +trucksNeededForCalculations).toFixed(2);
  }, [hoursPerTruckOverride, totalHaulingHoursOverride, trucksNeededForCalculations]);

  const totalHaulingHoursCalc = useMemo(() => {
    if (proposalDefaults.estimateTruckingByFullDay) {
      const jobDurationInDaysCeil = Math.ceil(jobDurationInDays);
      const calculatedHours =
        jobDurationInDaysCeil * averageWorkingHoursPerDay * trucksNeededForCalculations;

      return effectiveTotalHaulingHoursOverride || calculatedHours.toFixed(2);
    }
    return (
      effectiveTotalHaulingHoursOverride ||
      (+trucksNeededForCalculations * +totalTime * +tripsPerTruck.toFixed(1)).toFixed(2)
    );
  }, [
    effectiveTotalHaulingHoursOverride,
    trucksNeededForCalculations,
    jobDurationInDays,
    averageWorkingHoursPerDay,
    totalTime,
    tripsPerTruck,
    proposalDefaults.estimateTruckingByFullDay,
  ]);
  const totalHourlyCost = useMemo(() => {
    return (
      +totalHaulingHoursCalc * (parsedRateOverride !== null ? parsedRateOverride * 100 : row.rate)
    ).toFixed(2);
  }, [totalHaulingHoursCalc, parsedRateOverride, row.rate]);

  const totalCalculatedCost = useMemo(() => {
    if (proposalPlantProduct?.rateOnWeight) {
      return new Decimal(
        parsedRateOverride ?? new Decimal(row.rate).div(100).toNumber()
      )
        .times(materialNeeded)
        .times(100)
        .ceil()
        .toNumber();
    }

    return +totalHourlyCost / 100;
  }, [
    proposalPlantProduct?.rateOnWeight,
    parsedRateOverride,
    row.rate,
    materialNeeded,
    totalHourlyCost,
  ]);

  const displayedCostDollars = useMemo(() => {
    if (proposalPlantProduct?.rateOnWeight) {
      return +totalCalculatedCost / 100;
    }
    return +totalHourlyCost / 100;
  }, [proposalPlantProduct?.rateOnWeight, totalCalculatedCost, totalHourlyCost]);
  const hasManualTotalCost = totalCost !== null && totalCost !== "";
  const displayedCostValue = hasManualTotalCost
    ? totalCost
    : displayedCostDollars.toFixed(2);
  const hoursPerTruckCalc = useMemo(() => {
    if (!+trucksNeededForCalculations) {
      return "0.00";
    }
    return (+totalHaulingHoursCalc / +trucksNeededForCalculations).toFixed(2);
  }, [totalHaulingHoursCalc, trucksNeededForCalculations]);

  const rightAlign = {
    "& .MuiInputBase-root": {
      "& input": {
        textAlign: "right",
      },
    },
  };
  const currencyStartAdornment = {
    InputProps: {
      startAdornment: (
        <InputAdornment position="start" sx={{ "& .MuiTypography-root": { fontSize: "0.75rem" } }}>
          $
        </InputAdornment>
      ),
    },
  };

  return (
    <TableRow
      key={row.id}
      sx={{ "&:last-child td, &:last-child th": { border: 0 }, height: "auto" }}
    >
      <TableCell
        component="th"
        scope="row"
        sx={{
          maxWidth: compactMode ? 96 : 120,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: "#555",
        }}
      >
        {row.name}
      </TableCell>
      <TableCell align="right" sx={{ textAlign: "center", color: "#555" }}>
        {row.tonnage}
      </TableCell>
      <TableCell align="right" sx={{ py: "1px", px: "6px", color: "#555" }}>
        <FormField
          disabled={inViewMode}
          align="right"
          mb={0}
          type="number"
          inputProps={{ min: 0, step: 1 }}
          placeholder={row.rate / 100}
          value={rateOverride}
          {...currencyStartAdornment}
          sx={{ rightAlign, py: "1px", px: "6px" }}
          onChange={(e) => setRateOverride(e.target.value)}
        />
      </TableCell>
      <TableCell align="right" sx={{ py: "1px", px: "6px", color: "#555" }}>
        <FormField
          disabled={inViewMode}
          align="right"
          mb={0}
          type="number"
          inputProps={{ min: 0, step: 0.1 }}
          placeholder={String(truckingProductionRateValue)}
          sx={{ rightAlign, py: "1px", px: "6px" }}
          onChange={(e) => {
            handleProductionRateChange(e.target.value);
          }}
        />
      </TableCell>
      <TableCell sx={{ py: "1px", px: "6px", color: "#555" }}>
        <FormField
          disabled={inViewMode}
          align="right"
          mb={0}
          type="text"
          placeholder={trucksNeededAuto.toFixed(1)}
          value={trucksNeededOverride}
          inputProps={{ min: 0, step: 0.1 }}
          sx={{ rightAlign, py: "1px", px: "6px" }}
          onChange={(e) => {
            handleTrucksNeededChange(e.target.value);
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ py: "1px", px: "6px", color: "#555" }}>
        <FormField
          disabled={inViewMode}
          align="right"
          mb={0}
          type="number"
          inputProps={{ min: 0, step: 0.1 }}
          placeholder={hoursPerTruckCalc}
          sx={{ rightAlign, py: "1px", px: "6px" }}
          value={hoursPerTruckOverride}
          onChange={(e) => {
            setHoursPerTruckOverride(e.target.value);
            if (e.target.value === "") {
              setTotalHaulingHoursOverride(null);
              return;
            }
            const hoursPerTruck = +e.target.value;
            if (Number.isFinite(hoursPerTruck)) {
              setTotalHaulingHoursOverride(
                (hoursPerTruck * +trucksNeededForCalculations).toFixed(2)
              );
            }
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ py: "1px", px: "6px", color: "#555" }}>
        {" "}
        <FormField
          disabled={inViewMode}
          align="right"
          mb={0}
          type="number"
          placeholder={totalHaulingHoursCalc}
          inputProps={{ min: 0, step: 0.1 }}
          sx={{ rightAlign, py: "1px", px: "6px" }}
          value={totalHaulingHoursOverride}
          onChange={(e) => {
            setTotalHaulingHoursOverride(e.target.value);
            if (e.target.value === "") {
              setHoursPerTruckOverride(null);
              return;
            }
            const totalHaulingHours = +e.target.value;
            if (Number.isFinite(totalHaulingHours) && +trucksNeededForCalculations) {
              setHoursPerTruckOverride(
                (totalHaulingHours / +trucksNeededForCalculations).toFixed(2)
              );
            }
          }}
        />
      </TableCell>
      {showAdvancedColumns && (
        <>
          <TableCell align="right" sx={{ padding: "1px", textAlign: "center", color: "#555" }}>
            {+totalTripsNeeded}
          </TableCell>
          <TableCell align="right" sx={{ padding: "1px", textAlign: "center", color: "#555" }}>
            {(+tripsPerTruck).toFixed(1)}
          </TableCell>
        </>
      )}
      <TableCell align="right" sx={{ py: "1px", px: "6px", color: "#555" }}>
        <FormField
          type="text"
          mb={0}
          fullWidth
          disabled={inViewMode}
          align="right"
          sx={{ rightAlign, py: "1px", px: "6px" }}
          value={displayedCostValue}
          {...currencyStartAdornment}
          onChange={(e) => setTotalCost(e.target.value === "" ? null : e.target.value)}
          inputProps={{ step: "0.01", min: "0" }}
        />
      </TableCell>

      <TableCell align="right" sx={{ py: "1px", px: "4px", color: "#555", whiteSpace: "nowrap" }}>
        <MDButton
          sx={{
            minHeight: "24px",
            height: "24px",
            minWidth: "24px",
            width: "24px",
            padding: 0,
            margin: "0 4px 0 0",
            fontSize: "14px",
            lineHeight: 1,
          }}
          color="blue"
          size="small"
          onClick={() =>
            handleSubmit({
              transferTruckId: row.transferTruckId,
              proposalProductId: product.id,
              proposalPlantProductId: proposalPlantProduct.id,
              rate: parsedRateOverride !== null ? parsedRateOverride * 100 : null,
              truckingProductionRate: toFiniteNumber(truckingProductionRateValue),
              quantity: +trucksNeededForCalculations,
              quantityOverride: trucksNeededOverride ? +trucksNeededOverride : null,
              totalHaulingHoursOverride: totalHaulingHoursOverride
                ? +totalHaulingHoursOverride
                : null,
              costOverride: hasManualTotalCost
                ? +totalCost * 100
                : Math.round(displayedCostDollars * 100),
            })
          }
          disabled={!trucksNeededForCalculations || totalCalculatedCost === 0}
        >
          +
        </MDButton>
        {hasBeenAdded && (
          <MDButton
            color="error"
            variant="contained"
            size="small"
            sx={{
              minHeight: "24px",
              height: "24px",
              minWidth: "24px",
              width: "24px",
              padding: 0,
              fontSize: "14px",
              lineHeight: 1,
            }}
            onClick={() => handleRemoveTruck(row.productTransferTruckId)}
          >
            -
          </MDButton>
        )}
      </TableCell>
    </TableRow>
  );
};

export default PlantProductTransferTruckRow;
